import React, { useState, useEffect } from "react";
import "./channelDetail.scss";
import { Button, Toast } from "antd-mobile";
import { useTranslation } from '@/i18n';
import { useParams, useHistory, useLocation } from "react-router-dom";
import { createNonceStr, md5, getLocationSearch } from "@/utils/utils";
import request from "@/utils/request";
import { freeCardChannelDetail, freeCardChannelPay, freeCardCheckBuyCondition, accountBalance } from "@/api";
import walletImg from "@/assets/images/wallet.png";
import wechatImg from "@/assets/images/wechat.png";
import checkboxImg from "@/assets/images/checkbox.png";

const FreeCommissionCardChannelDetail = () => {
  const t = useTranslation();
  const { freeCardChannelId } = useParams();
  const history = useHistory();
  const location = useLocation();
  
  // 从 URL 参数或 localStorage 获取 token
  let token = getLocationSearch().token || localStorage.getItem("userToken") || "";
  let appKey = "app_h5";
  let secret = "11dc72cf-88b0-46cd-91d7-b1a77fd69ee1";
  let timestamp = new Date().getTime();
  let random = createNonceStr();
  let tempStr = `appKey${appKey}secret${secret}timestamp${timestamp}random${random}token${token}`;
  let sign = md5(tempStr, 32).toString(16).toLocaleUpperCase();
  const params = {
    appKey: appKey,
    timestamp: timestamp,
    random: random,
    token: token,
    sign: sign,
  };

  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPurchased, setIsPurchased] = useState(false);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: "00",
    minutes: "00",
    seconds: "00",
  });
  
  // 支付相关状态
  const [payMethod, setPayMethod] = useState('wallet'); // 'wallet' | 'wechat'
  const [walletBalance, setWalletBalance] = useState(0);
  const [isBalanceSufficient, setIsBalanceSufficient] = useState(false);
  
  // 司机信息
  const [driverDetail, setDriverDetail] = useState(null);
  
  // 从 localStorage 获取司机信息
  useEffect(() => {
    try {
      const driverDetailStr = localStorage.getItem('driverDetail');
      if (driverDetailStr) {
        const detail = JSON.parse(driverDetailStr);
        setDriverDetail(detail);
      }
    } catch (error) {
      console.error('解析司机信息失败', error);
    }
  }, []);

  // 获取签名参数（用于需要最新时间戳的请求）
  const getSignParams = () => {
    const currentToken = getLocationSearch().token || localStorage.getItem("userToken") || "";
    const currentTimestamp = new Date().getTime();
    const currentRandom = createNonceStr();
    const currentTempStr = `appKey${appKey}secret${secret}timestamp${currentTimestamp}random${currentRandom}token${currentToken}`;
    const currentSign = md5(currentTempStr, 32).toString(16).toLocaleUpperCase();
    return {
      appKey: appKey,
      timestamp: currentTimestamp,
      random: currentRandom,
      token: currentToken,
      sign: currentSign,
    };
  };

  // 获取钱包余额
  const getWalletBalance = async (currentCardData = cardData) => {
    try {
      const signParams = getSignParams();
      const res = await request.get(accountBalance, { token: signParams.token }, signParams);
      if (res.data.errorCode === 0 && res.data.data) {
        const balance = res.data.data.balance || 0;
        setWalletBalance(balance);
        // 判断余额是否充足（使用钱包价格，单位是分）
        if (currentCardData && currentCardData.walletPrice) {
          setIsBalanceSufficient(balance >= currentCardData.walletPrice);
        } else if (currentCardData && currentCardData.sellPrice) {
          // 兼容旧逻辑（sellPrice单位是元）
          const priceInFen = +(+currentCardData.sellPrice * 100).toFixed(0);
          setIsBalanceSufficient(balance >= priceInFen);
        }
      }
    } catch (error) {
      console.error('获取钱包余额失败', error);
    }
  };

  // 检查购买条件
  const checkBuyCondition = async () => {
    Toast.loading(t('freeCard.buyModal.checkingCondition'), 0, () => {}, true);
    try {
      const signParams = getSignParams();
      // 使用 cardData 中的 freeCardId，如果没有则使用路由参数
      const freeCardId = cardData?.freeCardId || freeCardChannelId;
      const res = await request.post(
        `${freeCardCheckBuyCondition}${freeCardId}`,
        { token: signParams.token },
        signParams
      );
      Toast.hide();
      if (res.data.errorCode === 0) {
        if (res.data.data && res.data.data.result === true) {
          // 符合购买条件，执行购买
          handleConfirmBuy();
        } else {
          // 不符合购买条件
          const reason = res.data.data?.desc || t('freeCard.buyModal.notEligible');
          Toast.fail(reason, 3);
        }
      } else if (res.data.errorCode === 110004) {
        Toast.fail(t('common.loginRequired') || '请先登录', 2);
        setTimeout(() => {
          const currentPath = location.pathname + location.search;
          history.push(`/login?redirect=${encodeURIComponent(currentPath)}`);
        }, 2000);
      } else if (res.data.errorMsg) {
        Toast.fail(res.data.errorMsg, 2);
      }
    } catch (error) {
      Toast.hide();
      Toast.fail(t('common.networkError'), 2);
    }
  };

  // 获取免佣卡渠道详情
  const getCardDetail = async () => {
    setLoading(true);
    Toast.loading(t('common.loading'), 0, () => {}, true);
    try {
      const res = await request.post(
        `${freeCardChannelDetail}${freeCardChannelId}`,
        { token },
        params
      );
      Toast.hide();
      if (res.data.errorCode === 0 && res.data.data) {
        const data = res.data.data;
        setCardData(data);
        // 根据payType设置默认支付方式
        if (data.payType === 1) {
          // 仅微信支付
          setPayMethod('wechat');
        } else if (data.payType === 2) {
          // 仅钱包支付
          setPayMethod('wallet');
        }
        // payType === 3 时两者都支持，保持默认wallet
        // 获取余额时传入最新的cardData
        getWalletBalance(data);
      } else if (res.data.errorCode === 110004) {
        // 未登录，跳转到登录页
        Toast.fail(t('common.loginRequired') || '请先登录', 2);
        setTimeout(() => {
          // 保存当前路径，登录后跳回
          const currentPath = location.pathname + location.search;
          history.push(`/login?redirect=${encodeURIComponent(currentPath)}`);
        }, 2000);
      } else if (res.data.errorMsg) {
        Toast.fail(res.data.errorMsg, 2);
      }
    } catch (error) {
      Toast.hide();
      Toast.fail(t('common.networkError'), 2);
    }
    setLoading(false);
  };

  // 点击购买按钮，先检查购买条件
  const handleBuyClick = () => {
    if (isPurchased) {
      return;
    }
    checkBuyCondition();
  };

  // 确认购买
  const handleConfirmBuy = async () => {
    if (isPurchased) {
      return;
    }
    
    // 如果选择钱包支付，检查余额是否充足
    if (payMethod === 'wallet' && !isBalanceSufficient) {
      Toast.fail(t('freeCard.buyModal.insufficientWalletBalance'), 2);
      return;
    }
    
    Toast.loading(t('freeCard.buyModal.paying'), 0, () => {}, true);
    try {
      const signParams = getSignParams();
      // payType: 1.微信支付 2.钱包支付
      const payType = payMethod === 'wechat' ? 1 : 2;
      const res = await request.post(
        freeCardChannelPay,
        { 
          token: signParams.token,
          freeCardChannelId: Number(freeCardChannelId),
          payType: payType
        },
        signParams
      );
      Toast.hide();
      if (res.data.errorCode === 0) {
        if (payMethod === 'wechat' && res.data.data?.counter_url) {
          // 微信支付，跳转到收银台
          window.location.href = res.data.data.counter_url;
        } else {
          // 钱包支付成功
          Toast.success(t('freeCard.buyModal.paySuccess'), 2);
          setIsPurchased(true);
        }
      } else if (res.data.errorCode === 110004) {
        // 未登录，跳转到登录页
        Toast.fail(t('common.loginRequired') || '请先登录', 2);
        setTimeout(() => {
          // 保存当前路径，登录后跳回
          const currentPath = location.pathname + location.search;
          history.push(`/login?redirect=${encodeURIComponent(currentPath)}`);
        }, 2000);
      } else if (res.data.errorMsg) {
        Toast.fail(res.data.errorMsg, 2);
      }
    } catch (error) {
      Toast.hide();
      Toast.fail(t('common.networkError'), 2);
    }
  };

  // 计算倒计时
  const calculateCountdown = (expiryDate) => {
    const now = new Date().getTime();
    const distance = expiryDate - now;

    if (distance < 0) {
      return {
        days: 0,
        hours: "00",
        minutes: "00",
        seconds: "00",
      };
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = String(
      Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    ).padStart(2, "0");
    const minutes = String(
      Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
    ).padStart(2, "0");
    const seconds = String(
      Math.floor((distance % (1000 * 60)) / 1000)
    ).padStart(2, "0");

    return {
      days,
      hours,
      minutes,
      seconds,
    };
  };

  useEffect(() => {
    document.title = t('freeCard.channelDetailTitle') || '免佣卡详情';
    getCardDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freeCardChannelId]);

  useEffect(() => {
    if (!cardData || !cardData.freeEndDate) {
      return;
    }
    
    const expiryDate = new Date(cardData.freeEndDate).getTime();
    if (isNaN(expiryDate)) {
      return;
    }
    
    const interval = setInterval(() => {
      setCountdown(calculateCountdown(expiryDate));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [cardData]);

  if (loading || !cardData) {
    return (
      <div className="free-commission-card-channel-detail loading">
        <div className="loading-text">{t('common.loading')}</div>
      </div>
    );
  }

  // 支付成功页面
  if (isPurchased) {
    return (
      <div className="free-commission-card-channel-detail success-page">
        <div className="success-icon">✓</div>
        <div className="success-title">{t('freeCard.paySuccess.title') || '购买成功'}</div>
        <div className="success-desc">{t('freeCard.paySuccess.desc') || '您已成功购买免佣卡'}</div>
        
        <div className="card-info-box">
          <div className="info-item">
            <span className="label">{t('freeCard.card.name', { name: '' }) || '卡片名称'}：</span>
            <span className="value">{cardData.cardName}</span>
          </div>
          <div className="info-item">
            <span className="label">{t('freeCard.card.city') || '适用城市'}：</span>
            <span className="value">{cardData.cityName}</span>
          </div>
          <div className="info-item">
            <span className="label">{t('freeCard.card.freeDate') || '免佣日期'}：</span>
            <span className="value">
              {cardData.freeEndDate 
                ? `${cardData.freeDate} ~ ${cardData.freeEndDate}` 
                : cardData.freeDate}
            </span>
          </div>
          <div className="info-item">
            <span className="label">{t('freeCard.card.timeSlot') || '免佣时段'}：</span>
            <span className="value">{cardData.freeTimeSlot}</span>
          </div>
        </div>

        <div className="success-tips">
          {t('freeCard.paySuccess.tips') || '请在有效期内使用'}
        </div>
      </div>
    );
  }

  // 详情页面
  return (
    <div className="free-commission-card-channel-detail">
      <div className="card-header">
        <div className="card-name">{cardData.cardName}</div>
        <div className="city-name">{cardData.cityName}</div>
      </div>

      <div className="card-content">
        <div className="card-main">
          <div className="info-row">
            <div className="label">{t('freeCard.card.freeDate') || '免佣日期'}</div>
            <div className="value">
              {cardData.freeEndDate 
                ? `${cardData.freeDate} ~ ${cardData.freeEndDate}` 
                : cardData.freeDate}
            </div>
          </div>
          
          <div className="info-row">
            <div className="label">{t('freeCard.card.timeSlot') || '免佣时段'}</div>
            <div className="value">{cardData.freeTimeSlot}</div>
          </div>
        </div>

        {cardData.freeEndDate && (
          <div className="countdown-box">
            <div className="countdown-label">{t('freeCard.detail.validTimeLeft') || '距离失效还剩'}</div>
            <div className="countdown-time">
              <div className="time-item">
                <span className="num">{countdown.days}</span>
                <span className="unit">{t('freeCard.detail.days') || '天'}</span>
              </div>
              <span className="separator">:</span>
              <div className="time-item">
                <span className="num">{countdown.hours}</span>
                <span className="unit">{t('freeCard.detail.hours') || '时'}</span>
              </div>
              <span className="separator">:</span>
              <div className="time-item">
                <span className="num">{countdown.minutes}</span>
                <span className="unit">{t('freeCard.detail.minutes') || '分'}</span>
              </div>
              <span className="separator">:</span>
              <div className="time-item">
                <span className="num">{countdown.seconds}</span>
                <span className="unit">{t('freeCard.detail.seconds') || '秒'}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 司机信息卡片 */}
      {driverDetail && (
        <div className="driver-info-section">
          <div className="section-header">
            <div className="section-title">{t('freeCard.driverInfo.title') || '司机信息'}</div>
            <div 
              className="switch-account-btn"
              onClick={() => {
                const currentPath = location.pathname + location.search;
                history.push(`/login?redirect=${encodeURIComponent(currentPath)}`);
              }}
            >
              {t('freeCard.driverInfo.switchAccount') || '切换账号'}
            </div>
          </div>
          <div className="driver-info-content">
            <div className="driver-info-row">
              <span className="label">{t('freeCard.driverInfo.name') || '司机姓名'}</span>
              <span className="value">{driverDetail.driverName || '-'}</span>
            </div>
            <div className="driver-info-row">
              <span className="label">{t('freeCard.driverInfo.phone') || '手机号码'}</span>
              <span className="value">{driverDetail.driverPhone || '-'}</span>
            </div>
            <div className="driver-info-row">
              <span className="label">{t('freeCard.driverInfo.vehicleNo') || '车牌号'}</span>
              <span className="value">{driverDetail.vehicleNo || '-'}</span>
            </div>
          </div>
        </div>
      )}

      {/* 支付方式选择区域 */}
      <div className="pay-method-section">
        <div className="section-title">{t('freeCard.buyModal.selectPayMethod') || '请选择支付方式'}</div>
        
        {/* 司机钱包选项 - payType: 2或3时显示 */}
        {(cardData.payType === 2 || cardData.payType === 3) && (
          <div 
            className={`pay-option ${payMethod === 'wallet' ? 'selected' : ''}`}
            onClick={() => setPayMethod('wallet')}
          >
            <div className="pay-option-left">
              <img src={walletImg} alt="wallet" className="pay-icon" />
              <div className="pay-option-info">
                <div className="pay-option-name">
                  {t('freeCard.buyModal.driverWallet') || '司机钱包'}
                  {cardData.walletPrice !== undefined && (
                    <span className="pay-price">¥{(cardData.walletPrice / 100).toFixed(2)}</span>
                  )}
                </div>
                <div className={`pay-option-balance ${!isBalanceSufficient ? 'insufficient' : ''}`}>
                  {t('freeCard.buyModal.balance', { balance: (walletBalance / 100).toFixed(2) }) || `余额：¥${(walletBalance / 100).toFixed(2)}`}
                  {!isBalanceSufficient && (
                    <span className="insufficient-tip">
                      {' '}({t('freeCard.buyModal.insufficientWalletBalance') || '余额不足'})
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="pay-option-right">
              {payMethod === 'wallet' && <img src={checkboxImg} alt="selected" className="check-icon" />}
            </div>
          </div>
        )}
        
        {/* 微信支付选项 - payType: 1或3时显示 */}
        {(cardData.payType === 1 || cardData.payType === 3) && (
          <div 
            className={`pay-option ${payMethod === 'wechat' ? 'selected' : ''}`}
            onClick={() => setPayMethod('wechat')}
          >
            <div className="pay-option-left">
              <img src={wechatImg} alt="wechat" className="pay-icon" />
              <div className="pay-option-info">
                <div className="pay-option-name">
                  {t('freeCard.buyModal.wechatPay') || '微信支付'}
                  {cardData.weChatPrice !== undefined && (
                    <span className="pay-price">¥{(cardData.weChatPrice / 100).toFixed(2)}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="pay-option-right">
              {payMethod === 'wechat' && <img src={checkboxImg} alt="selected" className="check-icon" />}
            </div>
          </div>
        )}
      </div>

      <div className="bottom-action">
        <Button
          className="buy-btn"
          type="warning"
          onClick={handleBuyClick}
          disabled={payMethod === 'wallet' && !isBalanceSufficient}
        >
          {t('freeCard.buyModal.buyNow') || '立即购买'}
        </Button>
      </div>
    </div>
  );
};

export default FreeCommissionCardChannelDetail;
